import type {
	CustomerCart,
	CustomerCartStatus,
	CustomerCartView,
	MultiCartResponse,
	SessionContext
} from "../model/CustomerCartTypes";
import { mapToQuery, arrayToQuery, url as encodeUrl } from "../utils/UrlUtil";
import { Request } from "../stores/Requests20";
import { handleServiceResponse } from "./ServerRequestsInternal";
const BASE_URL = "/ordercapture/customercart";
const TIMEOUT = 5000;
type GetMultiCartsRequest = {
	country: string;
	lastModified?: string;
	modifiedByMe?: string;
	nonEmpty?: string;
	deliveryDates: Set<string>;
	customerIds: Set<string>;
	storeIds: Set<string>;
	page: number;
	status?: CustomerCartStatus;
};
type GetCartRequest = {
	sessionContext: SessionContext;
	cartId: string;
};
type GetCurrentCartRequest = {
	sessionContext: SessionContext;
};
type GetEventsRequest = {
	sessionContext: SessionContext;
	cartId: string;
	eventIds: Array<string>;
};
type GetCartVersionRequest = {
	sessionContext: SessionContext;
	cartId: string;
};
type CreateCartRequest = {
	sessionContext: SessionContext;
	requestId: string;
	isEmployeeCart?: boolean | null;
};
type SaveCartRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	cartName: string;
	clearCart: boolean;
};
type DeleteCartRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
};
type AddItemRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
	quantity: number;
	comment?: string | null;
};
type AddItemBatchRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	items: Record<
		string,
		{
			bundleId: string;
			quantity: number;
			comment: string | null | undefined;
			sortingIndex: number;
		}
	>;
	sourceCartId?: string | null;
};
type PutItemQuantityRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
	quantity: number;
};
type PutItemServiceOptionsRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
	serviceOptionIds: Array<string>;
};
type PutItemCommentRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
	comment: string;
};
type DeleteItemRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
};
type SetManualPriceRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	bundleId: string;
	newPrice: number;
	reset: boolean;
	isBbd: boolean;
};
type SetDeliveryDateRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	deliveryDate: string;
};
type SetOrderOptionsRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	sendConfirmationToCustomer?: boolean | null;
	sendConfirmationToCardholder?: boolean | null;
	externalCommissioning?: boolean | null;
	ignoreMinOrderValue?: boolean | null;
	singleOrder?: boolean | null;
	applyEgalisationTax?: boolean | null;
};
type SetPaymentOptionsRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	paymentType: string | null | undefined;
	paymentAllowance: string | null | undefined;
};
type SetOrderSourceRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	orderSource: string | null | undefined;
};
type SetOrderCommentsRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	general?: string | null;
	picking?: string | null;
	delivery?: string | null;
	invoice?: string | null;
};
type SetOwnOrderNumberRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	ownOrderNumber: string | null | undefined;
};
type SetOwnStoreNumberRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	ownStoreNumber: string | null | undefined;
};
type CheckoutRequest = {
	sessionContext: SessionContext;
	requestId: string;
	cartId: string;
	employeeOrderData?: {
		deliveryDate: string | null | undefined;
		orderComments: {
			general: string;
			delivery: string;
			invoice: string;
			picking: string;
		};
		payment: {
			paymentType: string | null | undefined;
			paymentAllowance: string | null | undefined;
		};
		orderSource: string | null | undefined;
		orderOptions: {
			sendConfirmationToCustomer: boolean;
			sendConfirmationToCardholder: boolean;
			externalCommissioning: boolean;
			ignoreMinOrderValue: boolean;
			singleOrder: boolean;
			applyEgalisationTax: boolean;
		};
	};
};
type CartVersionResponse = {
	cartId: string;
	cartVersion: number;
	eventHistory: string[];
};
type GetCartsRequest = {
	storeId: string;
	customerId: string;
	country: string;
	status: CustomerCartStatus;
};
type CheckoutResponse = {
	checkoutId?: string;
};
export async function getCarts(params: GetCartsRequest): Promise<Array<CustomerCartView>> {
	const url = `${BASE_URL}/carts?${getCartsQueryParams(params)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data.carts;
}
export async function getMultiCarts(request: GetMultiCartsRequest): Promise<MultiCartResponse> {
	const url = `${BASE_URL}/carts?${getMultiCartParams(request)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getCart(params: GetCartRequest, status: CustomerCartStatus = "ACTIVE"): Promise<CustomerCart> {
	const url = `${BASE_URL}/carts/${params.cartId}?${getQueryParams(params.sessionContext)}&status=${status}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getCartTechnical(cartId: string): Promise<CustomerCart> {
	const url = `${BASE_URL}/carts/${cartId}?locale=en-US`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getEventsTechnical(params: {
	cartId: string;
	eventIds: Array<string>;
}): Promise<Array<Record<string, any>>> {
	const url = `${BASE_URL}/carts/${params.cartId}/events/technical?locale=en-US&${arrayToQuery(
		params.eventIds,
		"eventIds"
	)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getCurrentCart(params: GetCurrentCartRequest): Promise<CustomerCart> {
	const url = `${BASE_URL}/carts/alias/current?${getQueryParams(params.sessionContext)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getEvents(params: GetEventsRequest): Promise<Array<Record<string, any>>> {
	const url =
		`${BASE_URL}/carts/${params.cartId}/events?${getQueryParams(params.sessionContext)}` +
		`&${arrayToQuery(params.eventIds, "eventIds")}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data.events;
}
export async function getCartVersion(params: GetCartVersionRequest): Promise<CartVersionResponse> {
	const url = `${BASE_URL}/carts/${params.cartId}/version`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function postCart(params: CreateCartRequest): Promise<string> {
	const url = `${BASE_URL}/carts?sync=true`;
	const requestBody = {
		requestId: params.requestId,
		isEmployeeCart: params.isEmployeeCart,
		...params.sessionContext
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function saveCart(params: SaveCartRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/save?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		cartName: params.cartName,
		clearCart: params.clearCart
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function deleteCart(params: DeleteCartRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId
	};
	const response = Request.delete(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function postItem(params: AddItemRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		bundleId: params.bundleId,
		quantity: params.quantity,
		comment: params.comment
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function postItemBatch(params: AddItemBatchRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		type: "BATCH",
		items: params.items
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function postRestockCart(params: AddItemBatchRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		type: "RESTOCK",
		items: params.items,
		sourceCartId: params.sourceCartId
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function putItemQuantity(params: PutItemQuantityRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items/${params.bundleId}/quantity?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		quantity: params.quantity
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function putItemServiceOption(params: PutItemServiceOptionsRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items/${params.bundleId}/service-options?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		serviceOptionIds: params.serviceOptionIds
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function putItemComment(params: PutItemCommentRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items/${params.bundleId}/comment?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		comment: params.comment
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setManualPrice(params: SetManualPriceRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items/${params.bundleId}/manual-price?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		newPrice: params.newPrice,
		reset: params.reset,
		isBbd: params.isBbd
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function deleteItem(params: DeleteItemRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items/${params.bundleId}?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId
	};
	const response = Request.delete(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setDeliveryDate(params: SetDeliveryDateRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/delivery-date?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		deliveryDate: params.deliveryDate
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setOrderOptions(params: SetOrderOptionsRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/order-options?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		sendConfirmationToCustomer: params.sendConfirmationToCustomer,
		sendConfirmationToCardholder: params.sendConfirmationToCardholder,
		externalCommissioning: params.externalCommissioning,
		ignoreMinOrderValue: params.ignoreMinOrderValue,
		singleOrder: params.singleOrder,
		applyEgalisationTax: params.applyEgalisationTax
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setPaymentOptions(params: SetPaymentOptionsRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/payment-options?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		paymentType: params.paymentType,
		paymentAllowance: params.paymentAllowance
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setOrderSource(params: SetOrderSourceRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/source?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		orderSource: params.orderSource
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setOrderComments(params: SetOrderCommentsRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/comments?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		general: params.general,
		picking: params.picking,
		delivery: params.delivery,
		invoice: params.invoice
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setOwnOrderNumber(params: SetOwnOrderNumberRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/own-order-number?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		ownOrderNumber: params.ownOrderNumber
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function setOwnStoreNumber(params: SetOwnStoreNumberRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/own-store-number?${getQueryParams(
		params.sessionContext
	)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		ownStoreNumber: params.ownStoreNumber
	};
	const response = Request.put(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
export async function createCheckout(params: CheckoutRequest): Promise<CheckoutResponse> {
	const url = `${BASE_URL}/carts/${params.cartId}/order-data/checkout?${getQueryParams(
		params.sessionContext
	)}&sync=true`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		employeeOrderData: params.employeeOrderData
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}

function getQueryParams(sessionContext: SessionContext): string {
	return (
		encodeUrl`customerId=${sessionContext.customerId}&cardholderNumber=${sessionContext.cardholderNumber}&storeId=${sessionContext.storeId}&country=${sessionContext.country}&locale=${sessionContext.locale}` +
		(sessionContext.fsdAddressId ? encodeUrl`&fsdAddressId=${sessionContext.fsdAddressId}` : "")
	);
}

function getCartsQueryParams(params: GetCartsRequest): string {
	return encodeUrl`customerId=${params.customerId}&storeId=${params.storeId}&country=${params.country}&status=${params.status}`;
}

export function getMultiCartParams(request: GetMultiCartsRequest): string {
	const params = new Map();
	if (request.country) params.set("country", request.country);
	if (request.modifiedByMe) params.set("modifiedByMe", request.modifiedByMe);
	if (request.lastModified) params.set("lastModified", request.lastModified);
	if (request.nonEmpty) params.set("nonEmpty", request.nonEmpty);
	if (request.page >= 0) params.set("page", request.page);
	params.set("status", request.status || "ACTIVE");
	params.set("storeId", request.storeIds);
	params.set("customerId", request.customerIds);
	params.set("deliveryDate", request.deliveryDates);
	return mapToQuery(params);
}
export async function postLoadSavedCart(params: AddItemBatchRequest): Promise<void> {
	const url = `${BASE_URL}/carts/${params.cartId}/items?${getQueryParams(params.sessionContext)}`;
	const requestBody = {
		requestId: params.requestId,
		customerId: params.sessionContext.customerId,
		type: "LOAD_FROM_SAVED",
		items: params.items,
		savedCartId: params.sourceCartId
	};
	const response = Request.post(url).timeout(TIMEOUT).execute(requestBody);
	return (await handleServiceResponse(response)).data;
}
